import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  MenuManager,
} from '@components';
import {
  businessQuery,
  linklistQuery,
  linklistMutation,
  pagesQuery,
  siteQuery,
} from '@queries';
import { ChevronRightIcon } from '@svgs';

import { TemplateBreadcrumbs } from '../components/TemplateBreadcrumbs/TemplateBreadcrumbs.jsx';
import { TemplateSidebar } from '../components/TemplateSidebar/TemplateSidebar.jsx';

import '@nosferatu500/react-sortable-tree/style.css'; // This only needs to be imported once in your app
import './TemplateMenu.scss';

export const TemplateMenu = () => {
  const { t, i18n } = useTranslation();
  const { businessID, linklistID, siteID } = useParams();
  const queryClient = useQueryClient();

  // get business
  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  // get site/template
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // save site
  const mutation = useMutation(
    linklistMutation(linklistID, {
      onSuccess: (data) => {
        // invalidate the linklists list so it is fetched again
        queryClient.invalidateQueries({ queryKey: ['linklists', 'list'] });
        // invalidate detail view
        queryClient.invalidateQueries({
          queryKey: ['linklists', 'detail', linklistID],
        });
      },
    }),
  );

  // get pages
  const { data: pagesData, isPending: pagesIsPending } = useQuery(
    pagesQuery({
      site_id: siteID,
      order_by: 'pattern',
    }),
  );

  // get linklist
  // staleTime passed to disable stage cache reloads while editing
  const { data: linklistData, isPending: linklistIsPending } = useQuery(
    linklistQuery(linklistID, {
      staleTime: Infinity,
    }),
  );

  return (
    <>
      <Header />
      <BodyWrapper>
        <TemplateSidebar
          businessID={businessID}
          businessData={businessData}
          siteID={siteID}
          siteData={siteData}
        />
        <MainWrapper>
          <TemplateBreadcrumbs businessData={businessData} siteData={siteData}>
            <li className="sb-flex">
              <Link
                to={`/dashboard/projects/${businessID}/templates/${siteID}/menus`}
              >
                Menus
              </Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              {linklistIsPending && <span>...</span>}
              {!linklistIsPending && <span>{linklistData.name}</span>}
            </li>
          </TemplateBreadcrumbs>
          <div className="sb-manage-content">
            {(linklistIsPending || pagesIsPending) && (
              <>
                <h1>...</h1>
                <Loader className="sb-loader sb-loader-large" />
              </>
            )}
            {linklistData && pagesData && (
              <>
                <h1>Manage menu "{linklistData.name}"</h1>
                <MenuManager
                  linklistData={linklistData}
                  pagesData={pagesData}
                  disabled={mutation.isPending}
                  mutation={mutation}
                  siteData={siteData}
                />
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
